import { type FC, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageTitle } from "../../components/PageTitle";
import {
  type CreateDeliveryInput,
  useCreateDeliveryApi,
  useDeliveryApi,
  useSendDeliveryApi,
} from "../../hooks/use-delivery-api";
import { useSenderEmails } from "../../hooks/use-sender-emails-api";
import { DeliveryTiming } from "../../hooks/useEmailTemplatesRestApi";
import {
  DeliveryForm,
  type DeliveryFormValues,
} from "./components/DeliveryForm";

export const DeliveryNewPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copyFrom = searchParams.get("copy-from");

  const { createDelivery, loading: createLoading } = useCreateDeliveryApi();
  const { sendDelivery, loading: sendLoading } = useSendDeliveryApi();
  const { data: senderEmailsData } = useSenderEmails({ verifiedOnly: true });
  const { data: sourceDelivery, loading: sourceLoading } = useDeliveryApi(
    copyFrom || "",
  );

  const initialValues = useMemo(() => {
    if (!copyFrom || !sourceDelivery) return undefined;
    return {
      distributionGroupId: sourceDelivery.groupId ?? "",
      from: sourceDelivery.fromEmail,
      subject: sourceDelivery.subject,
      body: sourceDelivery.bodyText,
      includeSender: sourceDelivery.includeSender,
      deliveryTiming: DeliveryTiming.Now,
      scheduledAt: "",
    };
  }, [copyFrom, sourceDelivery]);

  const buildCreateInput = (
    values: DeliveryFormValues,
  ): CreateDeliveryInput => {
    const senders = senderEmailsData?.items ?? [];
    const sender = senders.find(s => s.email === values.from);
    return {
      groupId: values.distributionGroupId,
      fromEmail: values.from,
      fromName: sender?.displayName || undefined,
      subject: values.subject.trim(),
      bodyText: values.body.trim(),
      includeSender: values.includeSender,
      scheduledAt:
        values.deliveryTiming === DeliveryTiming.Scheduled
          ? new Date(values.scheduledAt).toISOString()
          : undefined,
    };
  };

  const handleSaveDraft = async (values: DeliveryFormValues) => {
    const input = buildCreateInput(values);
    await createDelivery({ ...input, scheduledAt: undefined });
    navigate("/deliveries");
  };

  const handleSend = async (values: DeliveryFormValues) => {
    const input = buildCreateInput(values);
    const delivery = await createDelivery(input);
    if (values.deliveryTiming === DeliveryTiming.Now) {
      await sendDelivery(delivery.id);
    }
    navigate("/deliveries");
  };

  if (copyFrom && sourceLoading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex justify-center py-12">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/deliveries" className="btn btn-ghost btn-sm">
          &larr; 戻る
        </Link>
        <PageTitle>新規メール配信</PageTitle>
      </div>

      <DeliveryForm
        key={copyFrom || "new"}
        initialValues={initialValues}
        saveDraftLabel="下書き保存"
        saveLoading={createLoading}
        sendLoading={sendLoading}
        onSaveDraft={handleSaveDraft}
        onSend={handleSend}
      />
    </div>
  );
};
